import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Command, Globe2, MessagesSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Sidebar } from "@/components/Sidebar";
import { MessageList } from "@/components/MessageList";
import { AssistantView } from "@/components/AssistantView";
import { ChatComposer } from "@/components/ChatComposer";
import { SettingsPanel } from "@/components/SettingsPanel";
import { GlobeView } from "@/components/GlobeView";
import { Switch } from "@/components/ui/switch";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { streamChat } from "@/lib/streamChat";
import { DEFAULT_PREFERENCES, getBackgroundStyle } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const FEEDBACK_ANIM = { initial: { opacity: 0, y: -10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 } };

const normalize = (t) =>
  t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[.,!¡¿?]/g, "").trim();

function detectVoiceCommand(rawText) {
  const t = normalize(rawText);
  if (/(nueva conversacion|nuevo chat|empezar de nuevo)/.test(t)) return "new";
  if (/(salir del modo asistente|modo normal|modo chat)/.test(t)) return "normal-mode";
  if (/(modo asistente|pantalla completa|activa el modo asistente)/.test(t)) return "assistant-mode";
  if (/(callate|silencio|detente|para de hablar|basta|deja de hablar)/.test(t)) return "stop";
  if (/(activa|enciende|pon).*(voz)/.test(t)) return "voice-on";
  if (/(desactiva|apaga|silencia|quita).*(voz)/.test(t)) return "voice-off";
  if (/(cerrar sesion|desconectar|cierra la sesion)/.test(t)) return "logout";
  return null;
}

export default function Chat() {
  const { user, logout } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [orbState, setOrbState] = useState("idle");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [assistantMode, setAssistantMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [commandFeedback, setCommandFeedback] = useState("");
  const [prefs, setPrefs] = useState(DEFAULT_PREFERENCES);
  const [memory, setMemory] = useState({ profile: "", memories: [] });
  const [view, setView] = useState("globe"); // "globe" | "chat"

  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const lastSpokenRef = useRef("");
  const sendingRef = useRef(false);   // synchronous lock against concurrent requests
  const genRef = useRef(0);           // generation token; bumped on every interrupt/new request
  const abortRef = useRef(null);
  const activeIdRef = useRef(null);

  const autoSpeak = prefs.voice_enabled;
  const continuousListening = prefs.continuous_listening;
  const bgStyle = getBackgroundStyle(prefs.background);

  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [scrollRef]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // ---- preferences ----
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/preferences");
        setPrefs({ ...DEFAULT_PREFERENCES, ...data });
      } catch (error) {
        logError("No se pudieron cargar las preferencias:", error);
      }
    })();
  }, []);

  const savePreference = useCallback(async (partial) => {
    setPrefs((p) => ({ ...p, ...partial }));
    try {
      await api.put("/preferences", partial);
    } catch (error) {
      logError("No se pudo guardar la preferencia:", error);
    }
  }, []);

  // ---- memory ----
  const loadMemory = useCallback(async () => {
    try {
      const { data } = await api.get("/memory");
      setMemory({ profile: data.profile || "", memories: data.memories || [] });
    } catch (error) {
      logError("No se pudo cargar la memoria:", error);
    }
  }, []);

  useEffect(() => { loadMemory(); }, [loadMemory]);

  const saveProfile = useCallback(async (profile) => {
    setMemory((m) => ({ ...m, profile }));
    try {
      await api.put("/memory/profile", { profile });
    } catch (error) {
      logError("No se pudo guardar el perfil:", error);
    }
  }, []);

  const deleteMemory = useCallback(async (id) => {
    setMemory((m) => ({ ...m, memories: m.memories.filter((x) => x.id !== id) }));
    try {
      await api.delete(`/memory/${id}`);
    } catch (error) {
      logError("No se pudo eliminar el recuerdo:", error);
    }
  }, []);

  // ---- conversations ----
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch (error) {
      logError("No se pudieron cargar las conversaciones:", error);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const openConversation = async (id) => {
    setActiveId(id);
    setSidebarOpen(false);
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setMessages(data.messages || []);
    } catch (error) {
      logError("No se pudo abrir la conversación:", error);
    }
  };

  const newConversation = () => {
    setActiveId(null);
    setMessages([]);
    setSidebarOpen(false);
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/conversations/${id}`);
      if (id === activeId) newConversation();
      loadConversations();
    } catch (error) {
      logError("No se pudo eliminar la conversación:", error);
    }
  };

  // ---- voice output (single audio at a time) ----
  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  const speak = useCallback(async (text, gen) => {
    try {
      stopSpeaking();
      lastSpokenRef.current = text;
      const { data } = await api.post("/voice/speak", { text });
      if (gen !== genRef.current) return; // a newer request superseded this one
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audioRef.current = audio;
      setOrbState("speaking");
      audio.onended = () => { if (gen === genRef.current) setOrbState("idle"); };
      await audio.play();
    } catch (error) {
      logError("No se pudo reproducir la voz de Kai:", error);
      if (gen === genRef.current) setOrbState("idle");
    }
  }, [stopSpeaking]);

  // Stop everything in flight: audio, the streaming request and the busy state.
  const interrupt = useCallback(() => {
    genRef.current += 1;
    if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* noop */ } abortRef.current = null; }
    stopSpeaking();
    sendingRef.current = false;
    setSending(false);
    setMessages((m) => m.map((msg) => (msg.streaming ? { ...msg, streaming: false } : msg)));
  }, [stopSpeaking]);

  // ---- streaming chat ----
  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content) return;
    // Any new request stops whatever Kai was saying/writing.
    interrupt();
    const myGen = genRef.current;
    sendingRef.current = true;
    setInput("");
    setSending(true);
    setOrbState("processing");

    const aTs = `kai-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { role: "user", content, ts: `local-${Date.now()}` },
      { role: "assistant", content: "", ts: aTs, streaming: true },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;
    let full = "";

    try {
      await streamChat({
        url: `${BACKEND_URL}/api/chat/stream`,
        body: { conversation_id: activeIdRef.current, message: content },
        signal: controller.signal,
        onMeta: (cid) => {
          if (genRef.current === myGen) { setActiveId(cid); activeIdRef.current = cid; }
        },
        onDelta: (delta) => {
          full += delta;
          if (genRef.current === myGen) {
            setMessages((m) => m.map((msg) => (msg.ts === aTs ? { ...msg, content: full } : msg)));
          }
        },
      });
      if (genRef.current === myGen) {
        setMessages((m) => m.map((msg) => (msg.ts === aTs ? { ...msg, streaming: false } : msg)));
        loadConversations();
        if (autoSpeak && full.trim()) speak(full, myGen);
        else setOrbState("idle");
      }
    } catch (error) {
      if (error.name !== "AbortError" && genRef.current === myGen) {
        logError("Error al comunicar con Kai:", error);
        setMessages((m) => m.map((msg) => (
          msg.ts === aTs ? { ...msg, content: "Lo siento, no he podido procesar la solicitud. Verifique la conexión del sistema.", streaming: false } : msg
        )));
        setOrbState("idle");
      }
    } finally {
      if (genRef.current === myGen) {
        sendingRef.current = false;
        setSending(false);
        abortRef.current = null;
      }
    }
  };

  const showFeedback = (label) => {
    setCommandFeedback(label);
    setTimeout(() => setCommandFeedback(""), 2200);
  };

  const handleTranscript = async (text) => {
    const cmd = detectVoiceCommand(text);
    if (!cmd) {
      await sendMessage(text);
      return;
    }
    interrupt();
    setOrbState("idle");
    switch (cmd) {
      case "new": newConversation(); showFeedback("Comando: nueva conversación"); break;
      case "assistant-mode": setAssistantMode(true); showFeedback("Comando: modo asistente"); break;
      case "normal-mode": setAssistantMode(false); showFeedback("Comando: modo normal"); break;
      case "stop": showFeedback("Comando: silencio"); break;
      case "voice-on": savePreference({ voice_enabled: true }); showFeedback("Comando: voz activada"); break;
      case "voice-off": savePreference({ voice_enabled: false }); showFeedback("Comando: voz desactivada"); break;
      case "logout": showFeedback("Comando: cerrar sesión"); logout(); break;
      default: break;
    }
  };

  // ---- continuous speech recognition (active listening + barge-in) ----
  const handleSpeechStart = useCallback(() => {
    // The user is speaking: Kai immediately stops talking/writing and listens.
    if (sendingRef.current || (audioRef.current && !audioRef.current.paused)) interrupt();
    setOrbState((s) => (s === "processing" ? s : "listening"));
  }, [interrupt]);

  const handleVoiceResult = useCallback((text) => {
    const n = normalize(text);
    if (n.length < 2) return;
    // Ignore Kai hearing its own voice (echo of the last spoken reply).
    if (lastSpokenRef.current && normalize(lastSpokenRef.current).includes(n)) return;
    handleTranscript(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { supported: voiceSupported, listening, start, stop } = useSpeechRecognition({
    lang: "es-ES",
    onResult: handleVoiceResult,
    onSpeechStart: handleSpeechStart,
  });

  useEffect(() => {
    if (!voiceSupported) return;
    if (continuousListening && view === "chat") start();
    else stop();
  }, [voiceSupported, continuousListening, view, start, stop]);

  // ---- fallback recording (browsers without SpeechRecognition) ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setOrbState("processing");
        const form = new FormData();
        form.append("audio", blob, "audio.webm");
        try {
          const { data } = await api.post("/voice/transcribe", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data.text) await handleTranscript(data.text);
          else setOrbState("idle");
        } catch (error) {
          logError("No se pudo transcribir el audio:", error);
          setOrbState("idle");
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setOrbState("listening");
    } catch (error) {
      logError("Acceso al micrófono denegado:", error);
      alert("No se pudo acceder al micrófono. Conceda los permisos necesarios.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const onMicClick = () => {
    if (voiceSupported) savePreference({ continuous_listening: !continuousListening });
    else if (recording) stopRecording();
    else startRecording();
  };

  return (
    <div className="aria-grid h-screen w-full flex overflow-hidden text-slate-200 relative" style={{ background: bgStyle }}>
      <Sidebar
        conversations={conversations}
        activeId={activeId}
        user={user}
        autoSpeak={autoSpeak}
        sidebarOpen={sidebarOpen}
        onNew={newConversation}
        onOpen={openConversation}
        onDelete={deleteConversation}
        onToggleSpeak={(v) => savePreference({ voice_enabled: v })}
        onLogout={logout}
        onClose={() => setSidebarOpen(false)}
        onOpenSettings={() => { setSettingsOpen(true); loadMemory(); }}
      />

      <main className="flex-1 flex flex-col relative z-10 min-w-0">
        <header className="h-16 border-b border-white/5 backdrop-blur-xl flex items-center justify-between px-4 md:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-slate-300" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
            <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-cyan-400/80">
              Sistema Kai en línea
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white/5 border border-white/10 rounded-full p-1">
              <button
                data-testid="view-globe"
                onClick={() => setView("globe")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  view === "globe" ? "bg-cyan-500/20 text-cyan-200 shadow-[0_0_12px_rgba(34,211,238,0.3)]" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                <Globe2 className="w-4 h-4" /> Mapa
              </button>
              <button
                data-testid="view-chat"
                onClick={() => setView("chat")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  view === "chat" ? "bg-cyan-500/20 text-cyan-200 shadow-[0_0_12px_rgba(34,211,238,0.3)]" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                <MessagesSquare className="w-4 h-4" /> Chat
              </button>
            </div>
            {view === "chat" && (
              <div className="flex items-center gap-2">
                <span className="hidden sm:block text-xs font-mono text-slate-400">Modo asistente</span>
                <Switch
                  data-testid="modo-asistente-toggle"
                  checked={assistantMode}
                  onCheckedChange={setAssistantMode}
                />
              </div>
            )}
          </div>
        </header>

        <AnimatePresence>
          {commandFeedback && (
            <motion.div
              {...FEEDBACK_ANIM}
              className="absolute top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-cyan-500/15 border border-cyan-400/40 text-cyan-200 px-4 py-2 rounded-full font-mono text-xs backdrop-blur-md shadow-[0_0_20px_rgba(34,211,238,0.3)]"
              data-testid="command-feedback"
            >
              <Command className="w-3.5 h-3.5" /> {commandFeedback}
            </motion.div>
          )}
        </AnimatePresence>

        {view === "globe" ? (
          <GlobeView active={view === "globe"} />
        ) : (
          <>
            <AnimatePresence mode="wait">
              {assistantMode ? (
                <motion.div
                  key="assistant"
                  {...FADE}
                  className="flex-1 flex flex-col items-center justify-center px-6"
                  data-testid="assistant-mode-view"
                >
                  <AssistantView orbState={orbState} messages={messages} />
                </motion.div>
              ) : (
                <motion.div
                  key="chat"
                  {...FADE}
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-4 md:px-8 py-6"
                  data-testid="messages-area"
                >
                  <MessageList messages={messages} orbState={orbState} sending={sending} />
                </motion.div>
              )}
            </AnimatePresence>

            <ChatComposer
              input={input}
              onInputChange={setInput}
              onSend={() => sendMessage()}
              voiceSupported={voiceSupported}
              listening={listening}
              recording={recording}
              onMicClick={onMicClick}
              sending={sending}
            />
          </>
        )}
      </main>

      <SettingsPanel
        open={settingsOpen}
        onOpenChange={(v) => { setSettingsOpen(v); if (v) loadMemory(); }}
        prefs={prefs}
        onChange={savePreference}
        memory={memory}
        onSaveProfile={saveProfile}
        onDeleteMemory={deleteMemory}
      />
    </div>
  );
}
